<script setup>
import isArray from 'lodash/isArray';
import { inject, ref } from "vue";
import { __, requestSearchedRoamingPlans } from "@/utils";

import Modal from "@components/modal.vue";

const emit = defineEmits(["numberPlan", "searchedPlans"]);
const { plan, packages } = defineProps({
  plan: Object,
  packages: Array,
});

const europe_rates = ref(roamingRates.europe_rates);
const global_rates = ref(roamingRates.global_rates);

// features
const durationUnit = 
{
	'days' : __('days'),
	'weeks' : __('weeks'),
	'months' : __('months'),
};

const planUnits = {
	'minutes' : __('minutes'),
	'sms' : __('SMS')
};

const durationFeatureText = (divider) => `\u200F${__( 'Valid for') + divider + plan?.duration + ' ' + durationUnit[plan?.duration_unit]}\u200F`;

const includedInPlan = plan?.volumes.map(item => {
	if(item.usage_type === 'sms') return item.volume + ' ' + planUnits[item.usage_type];
	else if(item.usage_type === 'call') return (planUnits[item.unit] ?  item.volume + ' ' + planUnits[item.unit] : item.volume + ' ' + item.unit);
  else if(item.usage_type === 'data' && plan.volumes.length === 3) return '';
	else return `\u200F${item.volume + item.unit.toUpperCase()}\u200F`
}).filter(item => item).join(' ו-');

const isActive = ref(false);
const isLoading = ref(false);
const features = ref([
	{'featured' : durationFeatureText(': ')},
  {'featured' : __('package price') + ': ' + plan.price + ' ₪'},
]);
const availableGB = ref(plan?.volumes?.[0]?.volume + plan?.volumes?.[0]?.unit.toUpperCase());
const roamingUsage = ref('');
const roamingTermsOfServices = ref(roamingRates.roaming_terms_of_services);

const {
  radioLocationType,
  location_type,
  mcc,
  setSelectedCountry,
  setPreventDoubleLoad,
  usage_type,
  countries,
  fetchGradientLinkPlans,
} = inject('search');

const usageTypes = 
{
	'data' : __('Data only'),
	'call' : __('SMS - Minutes'),
	'data_calls' : __('Surfing and talking'),
};

const volumes = plan?.volumes.map(item => item.usage_type);

if(volumes.length === 1){
	roamingUsage.value = usageTypes['data'];
} else if(volumes.length === 2){
	roamingUsage.value = usageTypes['call'];
} else {
	roamingUsage.value = usageTypes['data_calls'];
}

if(plan.volumes.length === 3) {
  features.value = [
    {
      'featured' : __('The program includes')+ ': ' + includedInPlan
    },
    ...features.value
  ]
}

const toggleIsActive = () => {
  isActive.value = !isActive.value;
};

const handleTitle = (text) => text.replace('MINUTES', '');

const isEU = plan?.name_eng.toLowerCase() === 'europe';
const isGlobal = plan?.name_eng.toLowerCase() === 'global';
const showGradientLink = !isEU && !isGlobal;

const handleLoadOtherPackages = async (country) => {
  let currentPackages = packages;
  const getAltLocationType = radioLocationType.filter((item) => item.id !== location_type.value);

  if(getAltLocationType.length === 0) return;

  if(!mcc.value && (isEU || isGlobal)) {
    return;
  }

  // This should trigger only if no country is selected (a.k.a when you visit the page for the first time)
  if(!mcc.value) {
    setPreventDoubleLoad(true);
    const selectedCountry = countries.value.find(item => item.countryMcc === country);
    setSelectedCountry(selectedCountry);
    currentPackages = await fetchGradientLinkPlans(selectedCountry.countryMcc);
    setPreventDoubleLoad(false)
  }

  isLoading.value = true;
  const properCountry = (isEU || isGlobal) ? mcc.value : country;
  const response = await requestSearchedRoamingPlans(properCountry, usage_type.value, getAltLocationType?.[0]?.id);
  isLoading.value = false;

  if(!response?.data) {
    return;
  }

  // If the response is not an array, return this happens when no new packages are present
  const responseValues = JSON.parse(response.data);
  if (!isArray(responseValues?.response)) {
    return;
  }

  // Populate the packages with the new packages
  const combinedPackages = [...currentPackages];
  responseValues?.response?.forEach((responseValue) => {
    const isPresent = combinedPackages.some(
      (pkg) => pkg.product_id === responseValue.product_id
    );
    if (!isPresent) {
      combinedPackages.push(responseValue)
    }
  });

  // Sort packages so europe and global are at the end
  combinedPackages.sort((a, b) => {
    if (a.name_eng.toLowerCase() === 'global') return 1;
    if (b.name_eng.toLowerCase() === 'global') return -1;
    if (a.name_eng.toLowerCase() === 'europe') return 1;
    if (b.name_eng.toLowerCase() === 'europe') return -1;
    return a.name_eng.localeCompare(b.name_eng);
  });
  
  emit('searchedPlans', { response: combinedPackages });
} 
</script>
<template>
  <div
    class="tile-plan tile-plan--small tile-plan--border tile-plan--roaming"
    :class="{
      'is-active': isActive,
    }"
  >
    <div  class="tile__badge-absolute" v-if="plan?.is_recommended">
        <strong class="badge"> חבילת <br/>מומלצת </strong><!-- /.badge -->
        
        <i>
            <img :src="`${theme_path.url}/resources/images/svg/badge-icon.svg`" alt="Recommended">
        </i>
    </div><!-- /.tile__badge-absolute -->

    <div href="#" class="tile__link">
      <header class="tile__head">
        <strong class="label">
          <i v-if="plan?.alpha2 && !isGlobal && !isEU" class="tile__head-icon">
            <img :src="`https://flagcdn.com/w40/${plan.alpha2.toLowerCase()}.png`" alt="">
          </i>

          <i v-if="isEU" class="tile__head-icon">
            <img :src="`${theme_path.url}/resources/images/svg/icon-eu.svg`" alt="">
          </i>

          <i v-if="isGlobal" class="tile__head-icon tile__head-icon-global">
            <img :src="`${theme_path.url}/resources/images/svg/icon-global.svg`" alt="">
          </i>
		  
          <span v-if="plan?.name_heb">{{ plan.name_heb }}</span>
        </strong>

        <div class="tile__title" v-if="availableGB">
          <strong>
            {{ handleTitle(availableGB) }}
          </strong>
          <p>
            {{ `\u200F${roamingUsage}\u200F` }}
          </p>
        </div>
        <!-- /.tile__title -->
      </header>

      <div class="tile__body">
        <div class="tile__price" v-if="plan?.price">
          <strong>{{ Number(plan.price) }}</strong>

          <span>
            <em>₪</em>
            בלבד!
          </span>
        </div>
        <div class="tile__list">
          <ul>
            <li v-if="plan.volumes.length === 3">
              <span>{{ includedInPlan }}</span>
            </li>
            <li>
              <span>{{ durationFeatureText('-') }}</span>
            </li>
            <li class="no-checkmark" v-if="showGradientLink">
              <a
                href="#"
                @click.prevent="handleLoadOtherPackages(plan.mcc?.[0])"
                class="link-arrows"
                :class="{'is-disabled' : isLoading}"
              >{{ __('for more programs in this destination') }}</a> 
            </li>
            <li class="no-checkmark" v-if="isEU && europe_rates">
              <a :href="europe_rates" target="_blank" class="link-arrows">
                לרשימת המדינות המלאה
              </a>
            </li>
            <li class="no-checkmark" v-if="isGlobal && global_rates">
              <a :href="global_rates" target="_blank" class="link-arrows">
                לרשימת המדינות המלאה
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <slot name="actions" v-bind="plan" v-if="$slots.actions" />

    <div class="tile__foot tile__foot--step">
      <a href="#" class="link-icon link-icon--small" @click.prevent="toggleIsActive">
        <img :src="`${theme_path.url}/resources/images/ico-tooltip.svg`" />
        {{ __("for package details") }}
      </a>
    </div>
    <Modal :isActive="isActive" @close="isActive = false" class="popup--plan popup--roaming">
      <template #head>
        <p v-if="plan?.name_heb">
          {{ __('Package details for') }}-<strong>{{ plan.name_heb }}</strong>
        </p>

        <h2 v-if="availableGB" class="gradient">
          {{ handleTitle(availableGB) }}
        </h2>
        <strong class="gradient">
          {{ `\u200F${roamingUsage}\u200F` }}
        </strong>
      </template>
      <template #body>
        <div class="popup__body-right" v-if="plan?.popup_text">{{ plan?.popup_text }}</div>
        <div class="tile__list popup__body-right" v-if="features">
          <h5>{{ __('Key points of the plan') }}</h5>
          <div
            v-for="(feature, featuredIndex) in features"
            :index="featuredIndex"
            :key="featuredIndex"
            class="popup__feature-item"
            :class="{
              'is-last': featuredIndex === features.length - 1,
            }"
          >
            {{ `\u200F${feature.featured}\u200F` }}
          </div>
          <slot name="popup_body" v-bind="plan" />
          
          <div v-if="isEU && europe_rates">
            <a :href="europe_rates" target="_blank" class="tile__additional-link link-arrow gradient">
              לרשימת המדינות המלאה
            </a>
          </div>
          <div v-if="isGlobal && global_rates">
            <a :href="global_rates" target="_blank" class="tile__additional-link link-arrow gradient">
              לרשימת המדינות המלאה
            </a>
          </div>
          <div v-if="roamingTermsOfServices">
            <a :href="roamingTermsOfServices" target="_blank" class="tile__additional-link link-arrow gradient">
              לתקנון החבילה
            </a>
          </div>
        </div>
      </template>
      <template #foot>
        <a href="#" class="btn" type="button" @click.prevent="toggleIsActive">
          {{ __("closed") }} </a
        ><!-- /.btn -->
      </template>
    </Modal>
  </div>
</template>
